import { z } from "zod";
import { artifactHashSchema, artifactKindSchema, artifactRefSchema } from "./artifacts";
import { designedLayoutSchema } from "./slide-design";

const identifier = z.string().trim().min(1).max(200);
const text = z.string().trim().min(1);

export const agentContextEnvelopeSchema = z.object({
  schemaVersion: z.literal("1.0.0"),
  contextVersion: z.literal("agent-context/1.0.0"),
  projectId: identifier,
  revisionId: identifier,
  request: z.object({
    objective: text.max(200_000),
    audience: text.max(500),
    sourceNames: z.array(text.max(1024)).max(20),
  }).strict(),
  evidenceRefs: z.array(artifactRefSchema).max(100),
  memory: z.object({
    acceptedArtifactHashes: z.array(artifactHashSchema).min(3).max(20),
    decisions: z.array(text.max(500)).max(40),
    constraints: z.array(text.max(500)).max(40),
  }).strict(),
  budget: z.object({
    maxImageRequests: z.number().int().min(3).max(30),
    maxConcurrentTools: z.number().int().min(1).max(3),
  }).strict(),
  guardrails: z.object({
    editableCriticalContent: z.literal(true),
    noTextInGeneratedImages: z.literal(true),
    untrustedInputs: z.literal(true),
  }).strict(),
}).strict();

export const visualPromptSchema = z.object({
  slideId: identifier,
  order: z.number().int().min(1).max(30),
  layout: designedLayoutSchema,
  prompt: text.min(300).max(4_000),
  negativePrompt: text.min(100).max(1_500),
  inputHash: artifactHashSchema,
}).strict();

export const visualPromptPlanSchema = z.object({
  schemaVersion: z.literal("1.0.0"),
  planId: identifier,
  projectId: identifier,
  revisionId: identifier,
  agentVersion: z.literal("visual-prompt-agent/1.0.0"),
  sourceHashes: z.array(artifactHashSchema).min(3).max(20),
  prompts: z.array(visualPromptSchema).min(3).max(30),
  createdAt: z.string().datetime({ offset: true }),
}).strict().superRefine((plan, context) => {
  const ids = new Set<string>();
  const orders = new Set<number>();
  for (const prompt of plan.prompts) {
    if (ids.has(prompt.slideId)) context.addIssue({ code: "custom", path: ["prompts"], message: "Slide IDs must be unique." });
    if (orders.has(prompt.order)) context.addIssue({ code: "custom", path: ["prompts"], message: "Slide orders must be unique." });
    ids.add(prompt.slideId); orders.add(prompt.order);
  }
  if (plan.prompts.some((prompt, index) => prompt.order !== index + 1)) {
    context.addIssue({ code: "custom", path: ["prompts"], message: "Visual prompts must be in contiguous slide order." });
  }
});

export const slideAssetBindingsSchema = z.object({
  image: artifactRefSchema.optional(),
  data: artifactRefSchema.optional(),
}).strict().refine((value) => Boolean(value.image || value.data), "At least one slide asset binding is required.");

export const agentToolObservationSchema = z.object({
  schemaVersion: z.literal("1.0.0"),
  status: z.enum(["running", "succeeded", "failed", "retry"]),
  summary: text.max(240),
  artifacts: z.array(z.object({ artifactId: identifier, kind: artifactKindSchema, hash: artifactHashSchema }).strict()).max(40),
  next_actions: z.array(text.max(160)).max(8),
  error: z.object({ code: z.string().regex(/^[A-Z_]+$/), retryable: z.boolean(), retryAfterMs: z.number().int().nonnegative().optional() }).strict().optional(),
  metrics: z.object({
    elapsedMs: z.number().int().nonnegative().optional(),
    model: identifier.optional(),
    requestIndex: z.number().int().positive().optional(),
    totalRequests: z.number().int().positive().optional(),
    inputHash: artifactHashSchema.optional(),
    outputHash: artifactHashSchema.optional(),
  }).strict().optional(),
}).strict();

export type AgentContextEnvelope = z.infer<typeof agentContextEnvelopeSchema>;
export type VisualPromptPlan = z.infer<typeof visualPromptPlanSchema>;
export type SlideAssetBindings = z.infer<typeof slideAssetBindingsSchema>;
export type AgentToolObservation = z.infer<typeof agentToolObservationSchema>;
